<template>
  <div class="job-actions-menu">
    <b-dropdown
      ref="dropdown"
      :text="triggerText"
      :toggle-class="[
        'job-actions-trigger',
        inline ? 'job-actions-trigger--inline' : 'job-actions-trigger--chip',
        !inline && isCancelled ? 'job-actions-trigger--faded' : null,
      ]"
      :variant="inline ? 'outline-secondary' : 'link'"
      :right="!inline"
      :size="inline ? 'sm' : 'sm'"
      :aria-label="`Actions for ${job ? job.title : 'this job'}`"
      no-caret
      no-flip
      menu-class="job-actions-list"
      @click.native.stop
      @show="loadSeriesCountsIfNeeded"
      @shown="$emit('menu-toggle', true)"
      @hidden="$emit('menu-toggle', false)"
    >
      <template #button-content>
        <span aria-hidden="true">⋯</span>
        <span v-if="inline" class="job-actions-trigger-label">Actions</span>
      </template>

      <template v-for="(group, groupIndex) in groups">
        <b-dropdown-divider
          v-if="groupIndex > 0"
          :key="`divider-${group.key}`"
        />
        <b-dropdown-header :key="`header-${group.key}`">
          {{ group.label }}
        </b-dropdown-header>
        <b-dropdown-item
          v-for="item in group.items"
          :key="item.key"
          :disabled="item.pending"
          :link-class="[
            item.danger ? 'job-action--danger' : null,
            item.pending ? 'job-action--pending' : null,
          ]"
          @click="onSelect(item)"
        >
          {{ item.label }}
          <span v-if="item.pending" class="job-action-pending-hint"
            >Checking…</span
          >
        </b-dropdown-item>
      </template>
    </b-dropdown>

    <timesheet-conflict-modal ref="conflictModal" />
  </div>
</template>

<script>
import TimesheetConflictModal from '@/components/schedule/TimesheetConflictModal.vue'
import scheduleJobActions from '@/mixins/scheduleJobActions'

export default {
  name: 'JobActionsMenu',

  components: { TimesheetConflictModal },

  mixins: [scheduleJobActions],

  props: {
    job: {
      type: Object,
      default: null,
    },
    // Inline renders the wider "⋯ Actions" button used in the edit modal's
    // footer; the default is the compact square trigger sitting on a chip.
    inline: {
      type: Boolean,
      default: false,
    },
    // Inline variant only: JobFormModal's own series-summary fetch is still
    // in flight. Lets `groups` mark cancel-series/reopen-series pending
    // instead of showing them fully clickable while counts are unknown.
    seriesLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Populated lazily (see loadSeriesCountsIfNeeded) only for the chip
      // variant — the modal's inline menu already gets these on `job` itself
      // from JobFormModal's actionsJob, which has the series summary loaded
      // up front. Keyed so reopening the same chip's menu doesn't refetch.
      seriesActionCounts: { cancellable_count: null, reopenable_count: null },
      seriesCountsFetchedFor: null,
      seriesCountsLoading: false,
    }
  },

  computed: {
    triggerText() {
      return this.inline ? '⋯ Actions' : '⋯'
    },

    isCancelled() {
      return this.job && this.job.status === 'cancelled'
    },

    // The job shape jobActionItems() actually reasons about: `job` itself,
    // topped up with the lazily-fetched series counts for the chip variant
    // (the inline/modal variant already carries them on `job`, so this is a
    // no-op there — job's own non-null value always wins).
    effectiveJob() {
      if (!this.job || this.inline) return this.job
      return {
        ...this.job,
        cancellable_count:
          this.job.cancellable_count != null
            ? this.job.cancellable_count
            : this.seriesActionCounts.cancellable_count,
        reopenable_count:
          this.job.reopenable_count != null
            ? this.job.reopenable_count
            : this.seriesActionCounts.reopenable_count,
      }
    },

    // Chip variant fetches its own counts (seriesCountsLoading); the inline
    // variant relies on JobFormModal's fetch, passed in via the prop.
    countsLoading() {
      return this.inline ? this.seriesLoading : this.seriesCountsLoading
    },

    // Grouped so scope can never be misread: everything under "Whole series"
    // touches more than the date the BA clicked on.
    groups() {
      const items = this.jobActionItems(this.effectiveJob, this.countsLoading)
      const jobItems = items.filter((i) => i.group === 'job')
      const seriesItems = items.filter((i) => i.group === 'series')

      const groups = []
      if (jobItems.length) {
        groups.push({ key: 'job', label: 'This job', items: jobItems })
      }
      if (seriesItems.length) {
        groups.push({
          key: 'series',
          label: this.seriesLabel,
          items: seriesItems,
        })
      }
      return groups
    },

    seriesLabel() {
      const count = this.job && this.job.series_job_count
      return count ? `Whole series · ${count} jobs` : 'Whole series'
    },
  },

  mounted() {
    // z-index on the owning chip only wins comparisons within its own
    // stacking context, and that context is nested inside the roster
    // <table> — it can't out-rank another <td>'s content painted as part
    // of a different table cell/row. Rather than fight that, move the
    // chip variant's menu to <body> once so it's never inside the table at
    // all; Popper still positions it against the trigger correctly since
    // it works off the DOM element, not its ancestry. Left in the table
    // for the modal's inline variant, where there's no cross-cell overlap
    // to escape.
    if (!this.inline) {
      this.$nextTick(() => {
        const menuEl = this.$refs.dropdown && this.$refs.dropdown.$refs.menu
        if (menuEl && menuEl.parentNode !== document.body) {
          document.body.appendChild(menuEl)
        }
      })
    }
  },

  beforeDestroy() {
    const menuEl = this.$refs.dropdown && this.$refs.dropdown.$refs.menu
    if (menuEl && menuEl.parentNode === document.body) {
      document.body.removeChild(menuEl)
    }
  },

  methods: {
    // Fired on the dropdown's 'show' event (before it opens) — cheap no-op
    // for a standalone job or the inline/modal variant, and skipped on
    // repeat opens of the same series (seriesCountsFetchedFor). Doesn't
    // block the open: the menu renders immediately with both series items
    // present but pending (null == "unknown" per jobActionItems()'s
    // when()/pending), then reactively resolves each to enabled or removed
    // once the fetch settles — no more clickable-then-yanked-away flicker.
    async loadSeriesCountsIfNeeded() {
      if (this.inline || !this.job || !this.job.recurrence_group_id) return
      if (this.seriesCountsFetchedFor === this.job.recurrence_group_id) return
      this.seriesCountsFetchedFor = this.job.recurrence_group_id

      this.seriesCountsLoading = true
      try {
        const data = await this.$store.dispatch(
          'schedule/getJobSeries',
          this.job.recurrence_group_id,
        )
        if (data) {
          this.seriesActionCounts = {
            cancellable_count: data.cancellable_count,
            reopenable_count: data.reopenable_count,
          }
        }
      } catch (e) {
        // Leave counts unknown on failure - both series items stay visible
        // (and, since loading is now false, clickable) rather than silently
        // hiding a working action over a network blip.
      } finally {
        this.seriesCountsLoading = false
      }
    },

    async onSelect(item) {
      if (item.pending) return

      // Converting a single job into a series opens the repeating form — the
      // menu doesn't own that flow, the form does.
      if (item.key === 'make-repeat') {
        this.$emit('make-repeat', this.job)
        return
      }

      const result = await this.runJobAction(item.key, this.job)
      this.$emit('done', { ...result, action: item.key, job: this.job })
    },
  },
}
</script>

<style>
/* 44px on touch so the trigger is a real target, and always visible: the
   roster's stacked card layout below xl has no hover at all, so a
   hover-revealed control there is simply undiscoverable. */
.job-actions-trigger.job-actions-trigger--chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #6b7280;
  font-size: 18px;
  line-height: 1;
  text-decoration: none;
}

@media (hover: hover) and (pointer: fine) {
  /* Day columns are ~120px wide, so every pixel the trigger reserves comes
     straight out of the job title next to it. */
  .job-actions-trigger.job-actions-trigger--chip {
    min-width: 26px;
    min-height: 26px;
    font-size: 16px;
  }
}

.job-actions-trigger.job-actions-trigger--chip:hover,
.job-actions-trigger.job-actions-trigger--chip:focus {
  border-color: #e5e7eb;
  background: #ffffff;
  color: #111827;
}

/* Fades the trigger only (never the opened menu, which sits alongside it,
   not underneath it — see .job-actions-list, unaffected by this), and only
   for the chip trigger: the inline "⋯ Actions" button lives in the edit
   modal's own footer, where a washed-out control reads as a broken modal,
   not a status hint — see !inline in the toggle-class binding above. */
.job-actions-trigger--faded {
  opacity: 0.6;
}

.job-actions-trigger.job-actions-trigger--inline {
  min-height: 38px;
  border-radius: 8px;
  font-weight: 600;
}

.job-actions-trigger-label {
  margin-left: 0.35rem;
}

/* Focus is carried by a border colour change with no added shadow, matching
   the form controls in DESIGN.md. */
.job-actions-trigger:focus {
  border-color: #2c66d5 !important;
  box-shadow: none !important;
}

.job-actions-list {
  min-width: 260px;
  /* A full series menu is ~380px tall; on a laptop viewport that fits neither
     above nor below the trigger, so it scrolls rather than overflowing the
     screen. */
  max-height: 60vh;
  overflow-y: auto;
  padding: 0.375rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.08);
}

.job-actions-list .dropdown-header {
  padding: 0.5rem 0.75rem 0.25rem;
  color: #9ca3af;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.job-actions-list .dropdown-item {
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  color: #111827;
  font-size: 0.875rem;
}

.job-actions-list .dropdown-item:active,
.job-actions-list .dropdown-item:hover,
.job-actions-list .dropdown-item:focus {
  background: #f3f4f6;
  color: #111827;
}

/* The only colour-coded tier: permanent deletion. Reversible actions stay
   neutral (DESIGN.md — two tiers, never three). */
.job-actions-list .dropdown-item.job-action--danger,
.job-actions-list .dropdown-item.job-action--danger:hover,
.job-actions-list .dropdown-item.job-action--danger:focus {
  color: #dc3545;
}

/* Series counts not resolved yet - shown but not actionable, so it can
   settle into either enabled or removed without a clickable item vanishing
   out from under the BA's cursor. */
.job-actions-list .dropdown-item.job-action--pending {
  color: #9ca3af;
  cursor: default;
}

.job-action-pending-hint {
  margin-left: 0.5rem;
  font-size: 0.75rem;
  font-style: italic;
}
</style>
